import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { appUrl, hostKind, isSitePath } from "./hosts";
import { useAuth } from "./auth/AuthProvider";
import { useEffect, useState } from "react";
import { CompanyProvider, useCompany } from "./hooks/useCompany";
import useFreshVersion from "./hooks/useFreshVersion";
import ContactUs from "./pages/ContactUs";
import ManageEmployees from "./pages/ManageEmployees";
import PayrollHistory from "./pages/PayrollHistory";
import Dashboard from "./pages/Dashboard";
import WeeklyPayroll from "./pages/WeeklyPayroll";
import UserSettings from "./pages/UserSettings";
import CompanySettings from "./pages/CompanySettings";
import SingleCheck from "./pages/SingleCheck";
import PrintWeeklyChecks from "./pages/PrintWeeklyChecks.js";
import ManageSubscription from "./pages/ManageSubscription";
import SupportStart from "./pages/SupportStart";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import BankAccount from "./pages/BankAccount";
import CheckHistory from "./pages/CheckHistory";
import InvoiceList from "./pages/InvoiceList";
import CreateInvoice from "./pages/CreateInvoice";
import EstimateList from "./pages/EstimateList";
import CreateEstimate from "./pages/CreateEstimate";
import UpdateInvoice from "./pages/UpdateInvoice";
import UpdateEstimate from "./pages/UpdateEstimate";
import CategorizeTransactions from "./pages/CategorizeTransactions";
import GenerateAccountabilityStatement from "./pages/GenerateAccountabilityStatement";
import Generate1099 from "./pages/Generate1099";
import Generate1096 from "./pages/Generate1096";
import Filings1099List from "./pages/Filings1099List";
import Filings1096List from "./pages/Filings1096List";
import OnboardingWizard from "./components/OnboardingWizard";
import StripeCallback from "./pages/StripeCallback";
import PaymentSuccess from "./pages/PaymentSuccess";
import PayInvoice from "./pages/PayInvoice";
import SignIn from "./pages/SignIn";
import Sso from "./pages/Sso";
import { productPage } from "./auth/oleneSso";
import { STAGE } from "./stage";
import SignUp from "./pages/SignUp";
import './index.css';

// Intercept 403 subscription_required on the two write-blocked routes
// (POST /companies, POST /employees) and redirect to the subscription page.
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const response = await originalFetch(...args);
  if (response.status === 403) {
    const clone = response.clone();
    try {
      const data = await clone.json();
      const detail = data?.detail;
      const error = typeof detail === "object" ? detail?.error : null;
      if (error === "subscription_required" && window.location.pathname !== "/ManageSubscription") {
        window.location.href = "/ManageSubscription";
      }
    } catch {}
  }
  return response;
};

function App() {
  const auth = useAuth();
  const kind = hostKind();

  const handleSignOut = () => auth.signOut();

  // Site host (tspayclear.com): only landing pages live here. The edge 301s
  // app paths to app.<host>; this covers client-side navigation and any time
  // the edge function isn't attached.
  if (kind === "site" && !isSitePath(window.location.pathname)) {
    const { pathname, search, hash } = window.location;
    window.location.replace(appUrl(pathname + search + hash));
    return null;
  }

  if (auth.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-12 w-12 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-400 text-sm">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <CompanyProvider>
    <Router>
      <FreshVersion />
      <AppLoader />
      <OnboardingWizard />
      <AccessGate>
      <Routes>
        <Route path="/sso" element={<Sso />} />
        <Route path="/signin" element={auth.isAuthenticated ? <Navigate to="/" replace /> : <SignIn />} />
        <Route path="/signup" element={auth.isAuthenticated ? <Navigate to="/" replace /> : <SignUp />} />
        <Route path="/" element={
          kind === "site" ? <FrontPage />
            : auth.isAuthenticated ? <Dashboard auth={auth} handleSignOut={handleSignOut} />
            : kind === "app" ? <SignIn /> : <FrontPage />
        } />
        <Route path="/ManageSubscription" element={<RequireAuth><ManageSubscription /></RequireAuth>} />
        {/* Olene staff opening a customer's company (pages/SupportStart.js). */}
        <Route path="/support/:id" element={<RequireAuth><SupportStart /></RequireAuth>} />
        <Route path="/ManageEmployees" element={<RequireAuth><ManageEmployees /></RequireAuth>} />
        <Route path="/PayrollHistory" element={<RequireAuth><PayrollHistory /></RequireAuth>} />
        <Route path="/ManageWeeklyPayroll" element={<RequireAuth><WeeklyPayroll /></RequireAuth>} />
        <Route path="/PrintWeeklyPayroll" element={<RequireAuth><PrintWeeklyChecks /></RequireAuth>} />
        {/* The same entry paths in every Olene app (olene-platform docs/app-standard.md §2). */}
        <Route path="/settings" element={<RequireAuth><UserSettings /></RequireAuth>} />
        <Route path="/UserSettings" element={<Navigate to="/settings" replace />} />
        <Route path="/login" element={<Navigate to="/signin" replace />} />
        <Route path="/CompanySettings" element={<RequireAuth><CompanySettings /></RequireAuth>} />
        <Route path="/SingleCheck" element={<RequireAuth><SingleCheck /></RequireAuth>} />
        <Route path="/Success" element={<RequireAuth><SubscriptionSuccess /></RequireAuth>} />
        <Route path="/BankAccount" element={<RequireAuth><BankAccount /></RequireAuth>} />
        <Route path="/CheckHistory" element={<RequireAuth><CheckHistory /></RequireAuth>} />
        <Route path="/Invoices" element={<RequireAuth><InvoiceList /></RequireAuth>} />
        <Route path="/CreateInvoice" element={<RequireAuth><CreateInvoice /></RequireAuth>} />
        <Route path="/Estimates" element={<RequireAuth><EstimateList /></RequireAuth>} />
        <Route path="/CreateEstimate" element={<RequireAuth><CreateEstimate /></RequireAuth>} />
        <Route path="/UpdateInvoice/:invoice_id" element={<RequireAuth><UpdateInvoice /></RequireAuth>} />
        <Route path="/UpdateEstimate/:estimate_id" element={<RequireAuth><UpdateEstimate /></RequireAuth>} />
        <Route path="/CategorizeTransactions" element={<RequireAuth><CategorizeTransactions /></RequireAuth>} />
        <Route path="/GenerateAccountabilityStatement" element={<RequireAuth><GenerateAccountabilityStatement /></RequireAuth>} />
        <Route path="/Generate1099" element={<RequireAuth><Generate1099 /></RequireAuth>} />
        <Route path="/Generate1096" element={<RequireAuth><Generate1096 /></RequireAuth>} />
        <Route path="/Filings1099" element={<RequireAuth><Filings1099List /></RequireAuth>} />
        <Route path="/Filings1096" element={<RequireAuth><Filings1096List /></RequireAuth>} />
        <Route path="/ContactUs" element={<ContactUs />} />
        <Route path="/StripeCallback" element={<RequireAuth><StripeCallback /></RequireAuth>} />
        <Route path="/PaymentSuccess" element={<PaymentSuccess />} />
        <Route path="/pay/:invoice_id" element={<PayInvoice />} />
      </Routes>
      </AccessGate>
    </Router>
    </CompanyProvider>
  );
}

// Protected pages show the in-app sign-in in place (the URL is kept, so the
// page renders once the user signs in).
function RequireAuth({ children }) {
  const auth = useAuth();
  return auth.isAuthenticated ? children : <SignIn />;
}

// Signed in to Olene, but the backend refused this account: PayClear's page on olenesoft.com,
// where they can see what it is and get it (docs/signin.md, "Moving between apps").
function AccessGate({ children }) {
  const auth = useAuth();
  const { accessDenied } = useCompany();
  const denied = auth.isAuthenticated && accessDenied;
  useEffect(() => { if (denied) window.location.replace(productPage("payclear", STAGE)); }, [denied]);
  return denied ? null : children;
}

function AppLoader() {
  const auth = useAuth();
  const { isInitializing } = useCompany();
  if (!auth.isAuthenticated || !isInitializing) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-white">
      <svg className="animate-spin h-12 w-12 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <p className="text-gray-400 text-sm">Loading your account…</p>
    </div>
  );
}

const FEATURES_EN = [
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5-3.87M9 20H4v-2a4 4 0 015-3.87m6-4.13a4 4 0 10-8 0 4 4 0 008 0z" /></svg>),
    title: "Employee Management",
    desc: "Add employees, set hourly or salary wages, and manage your whole team — all in one place.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>),
    title: "Weekly Payroll",
    desc: "Enter daily hours per employee and submit payroll in seconds — with a full editable history.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a5 5 0 00-10 0v2M5 9h14l1 12H4L5 9z" /></svg>),
    title: "Payroll Checks",
    desc: "Print professional payroll checks individually or run a full week’s batch — with complete check history.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6M3 17h18" /></svg>),
    title: "Invoices & Estimates",
    desc: "Create branded invoices and estimates, track payment status, download PDFs, and send them directly to clients.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>),
    title: "1099-NEC & 1096 Filing",
    desc: "Generate IRS-ready 1099-NEC forms for every contractor and auto-produce the matching 1096 transmittal — PDFs filled from your saved data.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 6h2" /></svg>),
    title: "Multiple Companies",
    desc: "Manage multiple business entities under one account — switch companies instantly from any page.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 17a4 4 0 100-8 4 4 0 000 8zm0 0v4m0-16V1m8 16l-3-3M3 17l3-3m12-8l-3 3M3 7l3 3" /></svg>),
    title: "Payroll Analytics",
    desc: "Visualize payroll costs over time with area charts spanning 3, 6, or 12 months — broken down per company.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>),
    title: "Bilingual Support",
    desc: "Full English & Spanish support — switch languages at any time from within the app.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" /></svg>),
    title: "Direct Support & Custom Requests",
    desc: "We're a small team that genuinely listens. Got a bug, a feature idea, or a workflow we haven't built yet? Reach out and we'll make it happen.",
  },
];

const FEATURES_ES = [
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5-3.87M9 20H4v-2a4 4 0 015-3.87m6-4.13a4 4 0 10-8 0 4 4 0 008 0z" /></svg>),
    title: "Gestión de Empleados",
    desc: "Agrega empleados, establece salarios por hora o fijos, y administra todo el equipo desde un solo lugar.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>),
    title: "Nómina Semanal",
    desc: "Ingresa horas diarias por empleado y envía la nómina en segundos — con historial completo y editable.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a5 5 0 00-10 0v2M5 9h14l1 12H4L5 9z" /></svg>),
    title: "Cheques de Nómina",
    desc: "Imprime cheques profesionales uno a la vez o procesa toda la semana en un solo lote — con historial completo.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6M3 17h18" /></svg>),
    title: "Facturas y Presupuestos",
    desc: "Crea facturas y presupuestos, rastrea pagos, descarga PDFs y envíalos directamente a tus clientes.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>),
    title: "Formularios 1099-NEC y 1096",
    desc: "Genera formularios 1099-NEC listos para el IRS por cada contratista y produce automáticamente el 1096 — todo desde tus datos guardados.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 6h2" /></svg>),
    title: "Múltiples Empresas",
    desc: "Gestiona varias empresas desde una sola cuenta — cambia entre ellas desde cualquier página.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 17a4 4 0 100-8 4 4 0 000 8zm0 0v4m0-16V1m8 16l-3-3M3 17l3-3m12-8l-3 3M3 7l3 3" /></svg>),
    title: "Analítica de Nómina",
    desc: "Visualiza costos de nómina con gráficos de área para 3, 6 o 12 meses — por empresa.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>),
    title: "Soporte Bilingüe",
    desc: "Soporte completo en inglés y español — cambia el idioma en cualquier momento desde la app.",
  },
  {
    icon: (<svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" /></svg>),
    title: "Soporte Directo y Solicitudes",
    desc: "Somos un equipo pequeño que escucha de verdad. ¿Tienes un error, una idea o algo que aún no hemos construido? Contáctanos y lo resolvemos.",
  },
];

const PLANS = [
  {
    name: "Monthly",
    price: "$39.99",
    period: "/ month",
    highlights: ["Unlimited payroll runs", "Invoice & estimate creation", "Check printing", "1099-NEC & 1096 tax forms", "Payroll history & analytics"],
    cta: "Get Started",
  },
  {
    name: "6-Month",
    price: "$199.99",
    period: "/ 6 months",
    highlights: ["Everything in Monthly", "Save ~$40 vs monthly", "Priority support"],
    cta: "Best Value",
    featured: true,
  },
];

function FrontPage() {
  const [lang, setLang] = useState(localStorage.getItem("language") || "en");

  const toggleLang = () => {
    const next = lang === "en" ? "es" : "en";
    setLang(next);
    localStorage.setItem("language", next);
  };

  const es = lang === "es";
  const FEATURES = es ? FEATURES_ES : FEATURES_EN;

  const screenshots = [
    "/assets/images/PayClearDashboard.png",
    "/assets/images/ManagePayroll.png",
    "/assets/images/CheckHistory.png",
    "/assets/images/InvoiceExample.png",
    "/assets/images/1099Example.png",
    "/assets/images/ManageCompanies.png",
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const prev = () => setCurrentIndex((currentIndex - 1 + screenshots.length) % screenshots.length);
  const next = () => setCurrentIndex((currentIndex + 1) % screenshots.length);

  // "Sign in" / "Get started" point at the app host (app.<host>); on
  // localhost they stay relative.
  const signInHref = appUrl("/signin");
  const signUpHref = appUrl("/signup");

  return (
    <div className="flex flex-col min-h-screen bg-white text-gray-800">

      {/* Domain banner */}
      <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-medium text-center py-2 px-4">
        {es
          ? "🌐 Nuestro dominio oficial es tspayclear.com — estamos negociando payclear.com y esperamos tenerlo pronto."
          : "🌐 Our official domain is tspayclear.com — we're currently negotiating for payclear.com and hope to have it soon."}
      </div>

      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <img src="./assets/images/Payclear800.png" alt="PayClear" className="h-8" />
          <div className="flex items-center gap-3">
            <a
              href="/ContactUs"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition px-4 py-2 rounded-lg hover:bg-gray-100"
            >
              {es ? "Contáctanos" : "Contact Us"}
            </a>
            <button
              onClick={toggleLang}
              className="text-sm font-medium text-gray-500 hover:text-gray-800 transition px-3 py-1.5 rounded-lg hover:bg-gray-100 border border-gray-200"
            >
              {es ? "🇺🇸 English" : "🇲🇽 Español"}
            </button>
            <a
              href={signInHref}
              className="text-sm font-semibold border border-gray-300 text-gray-700 hover:border-gray-400 hover:text-gray-900 hover:bg-gray-50 transition px-4 py-2 rounded-lg"
            >
              {es ? "Iniciar Sesión" : "Sign In"}
            </a>
            <a
              href={signUpHref}
              className="text-sm font-semibold bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition shadow-sm"
            >
              {es ? "Registrarse" : "Sign Up"}
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-24 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "radial-gradient(circle at 20% 60%, #22c55e 0%, transparent 50%), radial-gradient(circle at 80% 20%, #16a34a 0%, transparent 50%), radial-gradient(circle at 50% 90%, #15803d 0%, transparent 40%)"}} />
        <div className="relative max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 bg-green-500 bg-opacity-20 text-green-400 text-sm font-semibold px-5 py-2 rounded-full mb-6 border border-green-500 border-opacity-30">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            {es ? "Hecho para pequeñas empresas" : "Built for small businesses"}
          </span>
          <h1 className="text-5xl sm:text-6xl font-bold mb-5 leading-tight tracking-tight">
            {es
              ? <><span className="text-green-400">Nómina, facturas</span><br />y formularios de impuestos.</>
              : <><span className="text-green-400">Payroll, invoices</span><br />& tax forms.</>}
          </h1>
          <p className="text-lg text-gray-300 mb-10 max-w-xl mx-auto leading-relaxed">
            {es
              ? "Procesa nómina, imprime cheques, genera 1099/1096, envía facturas y gestiona múltiples empresas — todo desde un solo lugar."
              : "Run payroll, print checks, file 1099s, send invoices, and manage multiple companies — all from one dashboard."}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-12">
            <a href={signUpHref} className="text-center bg-green-500 hover:bg-green-600 text-white font-semibold py-3.5 px-9 rounded-xl transition text-base shadow-lg shadow-green-900/40">
              {es ? "Crear Cuenta" : "Get Started Free"}
            </a>
            <a href={signInHref} className="text-center bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-20 text-white font-semibold py-3.5 px-9 rounded-xl transition text-base">
              {es ? "Iniciar Sesión" : "Sign In"}
            </a>
          </div>
          {/* Stats strip */}
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-gray-400 border-t border-white border-opacity-10 pt-8">
            {[
              { val: es ? "Bilingüe" : "Bilingual", label: es ? "Inglés & Español" : "English & Spanish" },
              { val: "$39.99", label: es ? "por mes" : "per month" },
              { val: es ? "Sin contrato" : "No contract", label: es ? "Cancela cuando quieras" : "Cancel anytime" },
            ].map((s) => (
              <div key={s.val} className="flex flex-col items-center">
                <span className="text-white font-semibold text-base">{s.val}</span>
                <span className="text-xs">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* How it works */}
      <section className="py-16 px-6 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-10 text-gray-900">
            {es ? "Empieza en minutos" : "Up and running in minutes"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: es ? "Crea tu cuenta" : "Create your account",
                desc: es ? "Regístrate gratis y explora todas las funciones sin tarjeta de crédito." : "Sign up free and explore every feature — no credit card needed.",
              },
              {
                step: "2",
                title: es ? "Agrega tu empresa y empleados" : "Add your company & employees",
                desc: es ? "Configura tu empresa y agrega a tus empleados." : "Set up your company and add your employees.",
              },
              {
                step: "3",
                title: es ? "Gestiona todo desde un lugar" : "Run everything from one place",
                desc: es ? "Procesa nómina, envía facturas, genera formularios de impuestos y más." : "Run payroll, send invoices, file tax forms, and more.",
              },
            ].map((item) => (
              <div key={item.step} className="flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-full bg-green-500 text-white font-bold text-lg flex items-center justify-center mb-4 shadow-md shadow-green-200">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-2 text-gray-900">
            {es ? "Todo lo que necesitas para tu negocio" : "Everything your business needs"}
          </h2>
          <p className="text-center text-gray-500 mb-12">
            {es ? "Sin tarifas ocultas. Sin configuración complicada." : "No hidden fees. No complicated setup."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 relative">
                {f.badge && (
                  <span className="absolute top-4 right-4 bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {f.badge}
                  </span>
                )}
                <div className="w-11 h-11 rounded-xl bg-green-50 text-green-600 flex items-center justify-center mb-4">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-2 text-gray-900">
            {es ? "Míralo en acción" : "See it in action"}
          </h2>
          <div className="relative flex items-center justify-center">
            <button
              onClick={prev}
              className="absolute left-0 z-10 -translate-x-4 w-10 h-10 bg-white border border-gray-200 shadow rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-50 transition text-xl"
            >
              ‹
            </button>
            <div
              className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 cursor-pointer max-w-3xl w-full"
              onClick={() => setIsModalOpen(true)}
            >
              <img
                src={screenshots[currentIndex]}
                alt={`Screenshot ${currentIndex + 1}`}
                className="w-full object-contain"
              />
            </div>
            <button
              onClick={next}
              className="absolute right-0 z-10 translate-x-4 w-10 h-10 bg-white border border-gray-200 shadow rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-50 transition text-xl"
            >
              ›
            </button>
          </div>
          <div className="mt-5 flex justify-center gap-2">
            {screenshots.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-2 rounded-full transition-all ${idx === currentIndex ? "w-6 bg-green-500" : "w-2 bg-gray-300"}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" onClick={() => setIsModalOpen(false)}>
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setIsModalOpen(false)} className="absolute -top-10 right-0 text-white text-3xl leading-none hover:text-gray-300 transition">×</button>
            <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-10 text-white text-4xl hover:text-gray-300">‹</button>
            <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-10 text-white text-4xl hover:text-gray-300">›</button>
            <img src={screenshots[currentIndex]} alt={`Screenshot ${currentIndex + 1}`} className="w-full rounded-xl" />
          </div>
        </div>
      )}

      {/* Pricing */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-2 text-gray-900">
            {es ? "Precios simples y transparentes" : "Simple, transparent pricing"}
          </h2>
          <p className="text-center text-gray-500 mb-12">
            {es ? "Sin tarjeta de crédito para comenzar." : "No credit card required to get started."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 border ${plan.featured ? "bg-gray-900 border-gray-700 text-white shadow-xl ring-2 ring-green-500 ring-opacity-50" : "bg-white border-gray-100 text-gray-800 shadow-sm"}`}
              >
                {plan.featured && (
                  <span className="inline-block bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                    {es ? "Mejor Valor" : "Best Value"}
                  </span>
                )}
                <h3 className={`text-lg font-semibold mb-1 ${plan.featured ? "text-white" : "text-gray-900"}`}>{plan.name}</h3>
                <div className="flex items-end gap-1 mb-6">
                  <span className={`text-4xl font-bold ${plan.featured ? "text-green-400" : "text-gray-900"}`}>{plan.price}</span>
                  <span className={`text-sm mb-1 ${plan.featured ? "text-gray-400" : "text-gray-500"}`}>{plan.period}</span>
                </div>
                <ul className="space-y-2.5 mb-8">
                  {plan.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm">
                      <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.featured ? "text-green-400" : "text-green-500"}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={plan.featured ? "text-gray-300" : "text-gray-600"}>{h}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={signUpHref}
                  className={`block text-center w-full py-3 rounded-xl font-semibold text-sm transition ${plan.featured ? "bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-900/30" : "bg-gray-900 hover:bg-gray-800 text-white"}`}
                >
                  {es ? (plan.featured ? "Comenzar Ahora" : "Comenzar") : plan.cta}
                </a>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-400 mt-6">
            {es ? "Crea tu cuenta gratis — explora todas las funciones antes de suscribirte." : "Create a free account — explore every feature before subscribing."}
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "radial-gradient(circle at 30% 50%, #22c55e 0%, transparent 50%), radial-gradient(circle at 70% 50%, #16a34a 0%, transparent 50%)"}} />
        <div className="relative max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {es ? "¿Listo para simplificar tu negocio?" : "Ready to simplify your business?"}
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            {es
              ? "Únete a PayClear y deja de perder tiempo en hojas de cálculo."
              : "Join PayClear and stop wasting time on spreadsheets."}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a href={signUpHref} className="text-center bg-green-500 hover:bg-green-600 text-white font-semibold py-3.5 px-9 rounded-xl transition text-base shadow-lg shadow-green-900/40">
              {es ? "Crear Cuenta Gratis" : "Get Started Free"}
            </a>
            <a href={signInHref} className="text-center bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-20 text-white font-semibold py-3.5 px-9 rounded-xl transition text-base">
              {es ? "Iniciar Sesión" : "Sign In"}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400 text-sm py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col items-center md:items-start gap-1">
            <img src="./assets/images/Payclear800.png" alt="PayClear" className="h-6 opacity-60" />
            <span className="text-xs text-gray-600">&copy; {new Date().getFullYear()} PayClear Inc. {es ? "Todos los derechos reservados." : "All rights reserved."}</span>
          </div>
          <div className="flex gap-6 text-xs">
            <a href="/ContactUs" className="hover:text-white transition">{es ? "Contáctanos" : "Contact Us"}</a>
            <a href={signInHref} className="hover:text-white transition">{es ? "Iniciar Sesión" : "Sign In"}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Reloads a long-open tab onto the live version (hooks/useFreshVersion.js). */
function FreshVersion() {
  useFreshVersion();
  return null;
}

export default App;
